import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { FormattedMessage } from "react-intl";
import { Alert, Badge, Segmented, Select, Spinner, cx, useToast } from "../../components/ui";
import { useT, type Translate } from "../../i18n/I18nProvider";

type Value = S["PipelineSwitchValue"];
const INHERIT = "__inherit__";

/** How a switch value reads: on/off for stage switches and flags, the choice's label for a choice switch. */
function show(t: Translate, s: S["OrganizationPipelineSwitch"], value: Value | null | undefined): string {
  if (value === null || value === undefined) return t("admin.orgPipeline.inherit");
  if (typeof value === "boolean") return value ? t("admin.orgPipeline.on") : t("admin.orgPipeline.off");
  return s.choices.find((c) => c.value === value)?.label ?? value;
}

/** A company's overrides of the pipeline switches (platform admins only): inherit, or a value per switch. */
export function OrgPipeline({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const path = { params: { path: { org_id: orgId } } };
  const switches = useQuery({
    queryKey: ["org-pipeline", orgId],
    queryFn: () => unwrap(api.GET("/v1/admin/orgs/{org_id}/pipeline-switches", path)),
  });
  const update = useMutation({
    mutationFn: (overrides: Record<string, Value | null>) =>
      unwrap(api.PUT("/v1/admin/orgs/{org_id}/pipeline-switches", { ...path, body: { overrides } })),
    onSuccess: (data) => qc.setQueryData(["org-pipeline", orgId], data),
    onError: (e) => toast(errorMessage(e), true),
  });
  if (switches.isLoading) return <Spinner />;
  if (switches.error || !switches.data) return <Alert tone="danger">{errorMessage(switches.error)}</Alert>;
  const items = switches.data.items;
  const choose = (s: S["OrganizationPipelineSwitch"], value: Value | null) =>
    update.mutate({ [s.key]: value }, {
      onSuccess: () =>
        toast(
          value === null
            ? t("admin.orgPipeline.inheritsToast", { name: s.label })
            : t("admin.orgPipeline.overrideToast", { name: s.label, value: show(t, s, value) }),
        ),
    });
  return (
    <div className="stage-list">
      {items.map((s) => {
        const off = s.effective === false || s.effective === "off";
        const warning = s.kind === "choice" ? s.choices.find((c) => c.value === s.effective)?.warning : null;
        return (
          <div key={s.key} className={cx("switch-row", "org", s.kind !== "stage" && "sub", off && "off", s.forced && "locked")}>
            <div style={{ flex: 1 }}>
              <div className="row" style={{ gap: 8 }}>
                <b>{s.label}</b>
                <Badge tone={off ? "warn" : "ok"}>{show(t, s, s.effective)}</Badge>
                {s.forced ? (
                  <span className="small faint row" style={{ gap: 4 }}>
                    <Lock size={12} />{" "}
                    {s.override !== null
                      ? t("admin.orgPipeline.forcedKept", { value: show(t, s, s.global_value), override: show(t, s, s.override) })
                      : t("admin.orgPipeline.forced", { value: show(t, s, s.global_value) })}
                  </span>
                ) : (
                  s.override === null && <span className="small faint">{t("admin.orgPipeline.global", { value: show(t, s, s.global_value) })}</span>
                )}
              </div>
              <div className="small muted" style={{ marginTop: 2 }}>{s.description}</div>
              {warning && (
                <div style={{ marginTop: 6 }}>
                  <Alert tone="warn">{warning}</Alert>
                </div>
              )}
            </div>
            <div className={cx(s.forced && "is-locked")} aria-disabled={s.forced} title={s.forced ? t("admin.orgPipeline.forcedTitle") : undefined}>
              {s.kind === "choice" ? (
                <Select<string>
                  value={s.override === null ? INHERIT : String(s.override)}
                  disabled={s.forced || update.isPending}
                  options={[
                    { value: INHERIT, label: t("admin.orgPipeline.inheritOption", { value: show(t, s, s.global_value) }) },
                    ...s.choices.map((c) => ({ value: c.value, label: c.label })),
                  ]}
                  onChange={(v) => choose(s, v === INHERIT ? null : v)}
                />
              ) : (
                <Segmented<"inherit" | "on" | "off">
                  value={s.override === null ? "inherit" : s.override ? "on" : "off"}
                  options={[
                    { value: "inherit", label: t("admin.orgPipeline.inheritOption", { value: show(t, s, s.global_value) }) },
                    { value: "on", label: t("admin.orgPipeline.onOption") },
                    { value: "off", label: t("admin.orgPipeline.offOption") },
                  ]}
                  onChange={(c) => !s.forced && !update.isPending && choose(s, c === "inherit" ? null : c === "on")}
                />
              )}
            </div>
          </div>
        );
      })}
      <p className="hint card-body">
        <FormattedMessage id="admin.orgPipeline.help" values={{ link: (c) => <Link to="/admin/stages">{c}</Link> }} />
      </p>
    </div>
  );
}
